import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Paging, Table, Tooltip, usePaging } from '@maxtropy/components';
import { V2OperationAnalysisRevenuePostResponse, apiV2OperationAnalysisRevenuePost } from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { ColumnsType } from 'antd/es/table';
import { formatNumber, formatValue } from '../../utils';
import SectionHeader from '../SectionHeader';
import styles from './index.module.scss';

type RevenueRecord = NonNullable<V2OperationAnalysisRevenuePostResponse['records']>[number];

const formatChange = (change?: string) => change?.replace(/-?\d+(?:\.\d+)?(?=%)/g, value => formatNumber(value));

const exportRevenue = () => {
  window.open('/api/v2/operation-analysis/revenue/export', '_blank');
};

const TrendValue = ({ value, change, unit = '' }: { value?: number; change?: string; unit?: string }) => {
  const trendClassName = change?.includes('下降') ? styles.down : change?.includes('上升') ? styles.up : undefined;

  return (
    <div className={styles.trendValue}>
      <span>{formatValue(value, unit)}</span>
      {change && <em className={trendClassName}>{formatChange(change)}</em>}
    </div>
  );
};

const RevenueCalculation = () => {
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;
  const { data: records, loading } = useRequest(
    () =>
      apiV2OperationAnalysisRevenuePost({
        timestamp: Date.now(),
        page: pageOffset,
        size: pageSize,
      }).then(response => {
        setTotalCount(response.total ?? 0);
        return response.records ?? [];
      }),
    {
      refreshDeps: [pageOffset, pageSize],
    }
  );

  const columns: ColumnsType<RevenueRecord> = [
    { title: '时间范围', dataIndex: 'timeRange', width: 150 },
    {
      title: '月均需量(MW)',
      dataIndex: 'monthlyAverageDemand',
      width: 130,
      render: (value: number | undefined, record) => (
        <TrendValue value={value} change={record.monthlyAverageDemandChange} />
      ),
    },
    {
      title: '总需量(MW)',
      dataIndex: 'totalDemand',
      width: 180,
      render: (value: number | undefined, record) => <TrendValue value={value} change={record.totalDemandChange} />,
    },
    {
      title: (
        <span>
          降低需量-收益(元){' '}
          <Tooltip title="降低月均需量单价（32.80元/kW）×对应月数">
            <InfoCircleOutlined className={styles.helpIcon} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'reducedDemandRevenue',
      width: 170,
      render: (value?: number) => formatNumber(value),
    },
    {
      title: '弃光量(MWh)',
      dataIndex: 'curtailmentAmount',
      width: 130,
      render: (value: number | undefined, record) => (
        <TrendValue value={value} change={record.curtailmentAmountChange} />
      ),
    },
    {
      title: '弃光率',
      dataIndex: 'curtailmentRate',
      width: 160,
      render: (value: number | undefined, record) => (
        <TrendValue value={value} change={record.curtailmentRateChange} unit="%" />
      ),
    },
    {
      title: (
        <span>
          弃光收益(元){' '}
          <Tooltip title="降低弃光量×电价(0.24元/kW)">
            <InfoCircleOutlined className={styles.helpIcon} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'curtailmentRevenue',
      width: 150,
      render: (value: number | undefined, record) => (
        <span className={record.curtailmentRevenueWarning ? styles.disabledText : ''}>
          {record.curtailmentRevenueWarning ?? formatNumber(value)}
        </span>
      ),
    },
  ];

  return (
    <section className={styles.section}>
      <div className={styles.titleRow}>
        <SectionHeader title="收益计算" />
        <Button type="primary" onClick={exportRevenue}>
          导出
        </Button>
      </div>
      <Table
        className={styles.table}
        rowKey={record => record.timeRange ?? ''}
        columns={columns}
        dataSource={records}
        loading={loading}
        scroll={{ x: 1080 }}
        pagination={false}
      />
      <Paging className={styles.pagination} pagingInfo={pagingInfo} />
    </section>
  );
};

export default RevenueCalculation;
